import { Modal, ModalSizeEnum, PrimaryButton, Tooltip, toast } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import { createRuleEmail, createRuleSpecificEmail } from 'modules/automation-rules/email/api/rule-email-api';
import { FilterEmailApiInterface } from 'modules/automation-rules/types/automation-rule-filter-interface';
import React, { useState } from 'react';
import DashboardMailEditor from 'shared/components/dashboard-mail-editor';
import { UnsavedEmailsEnum } from 'shared/components/dashboard-mail-editor/enums/unsaved-emails-enum';
import { useUnsavedEmail } from 'shared/components/dashboard-mail-editor/hooks/use-unsaved-email';
import { AttachmentFileInterface, EmailBaseInterface, EmailErrorResponseContentInterface, EmailErrorType, EmailRecipientErrorResponseContentInterface, EmailRecipientInterface, UnsavedAttachmentFileInterface } from 'shared/components/dashboard-mail-editor/types/email-interface';
import { getMailingUpdateData } from 'shared/components/dashboard-mail-editor/utils/get-mailing-update-data';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { isValidEmail } from 'shared/utils/email-validation';
import { KeyedMutator } from 'swr';
import { AddIcon } from '@systemeio/ui/icons';
const defaultValues: EmailRecipientInterface = {
  body: '',
  subject: '',
  previewText: '',
  fromName: '',
  fromEmail: '',
  recipient: ''
};
const defaultErrors: EmailErrorType = {
  body: '',
  fromEmail: '',
  fromName: '',
  subject: '',
  previewText: '',
  common: []
};
interface RuleEmailCreateModalProps {
  withRecipient?: boolean;
  mutateEmailData: KeyedMutator<FilterEmailApiInterface[]> | undefined;
  mutateSpecificEmailData: KeyedMutator<FilterEmailApiInterface[]> | undefined;
  onSetNewEmail: (data: {
    id: number;
    subject: string;
  }) => void;
}
const RuleEmailCreateModal = ({
  withRecipient,
  mutateEmailData,
  mutateSpecificEmailData,
  onSetNewEmail
}: RuleEmailCreateModalProps) => {
  const {
    t
  } = useLocoTranslation();
  const [opened, setOpened] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [tempState, setTempState] = useState<EmailRecipientInterface>(defaultValues);
  const [errors, setErrors] = useState<EmailErrorType>(defaultErrors);
  const [attachmentsTempState, setAttachmentsTempState] = useState<(AttachmentFileInterface | UnsavedAttachmentFileInterface)[]>([]);
  const onCreate = async () => {
    try {
      if (tempState) {
        setErrors(defaultErrors);
        if (!isValidEmail(tempState.fromEmail)) {
          return setErrors(prev_3 => ({
            ...prev_3,
            fromEmail: t('dashboard.validation.email_not_valid')
          }));
        }
        if (withRecipient && !isValidEmail(tempState.recipient)) {
          return setErrors(prev_4 => ({
            ...prev_4,
            recipient: t('dashboard.validation.email_not_valid')
          }));
        }
        setIsFetching(true);
        const currentEmail_0 = getMailingUpdateData({
          fromEmail: tempState.fromEmail,
          fromName: tempState.fromName,
          subject: tempState.subject,
          previewText: tempState.previewText,
          body: tempState.body
        }, attachmentsTempState);
        if (withRecipient) {
          const emailResponse = await createRuleSpecificEmail({
            recipientAddress: tempState.recipient,
            mailing: {
              currentEmail: currentEmail_0
            }
          });
          if (mutateSpecificEmailData) {
            await mutateSpecificEmailData(data => {
              if (data) {
                return [{
                  id: emailResponse.data.id,
                  subject: emailResponse.data.mailing.currentEmail.subject
                }, ...data];
              }
            }, false);
          }
          onSetNewEmail({
            id: emailResponse.data.id,
            subject: emailResponse.data.mailing.currentEmail.subject
          });
        } else {
          const emailResponse_0 = await createRuleEmail(currentEmail_0);
          if (mutateEmailData) {
            await mutateEmailData(data_0 => {
              if (data_0) {
                return [{
                  id: emailResponse_0.data.currentEmail.id,
                  subject: emailResponse_0.data.currentEmail.subject
                }, ...data_0];
              }
            }, false);
          }
          onSetNewEmail({
            id: emailResponse_0.data.currentEmail.id,
            subject: emailResponse_0.data.currentEmail.subject
          });
        }
        toast.success(t('global.changes_saved'));
        await removeDbRecord();
        setIsFetching(false);
        setOpened(false);
      }
    } catch (e) {
      setIsFetching(false);
      if (e instanceof BadRequest) {
        if (withRecipient) {
          const errors_0 = e.errors as EmailRecipientErrorResponseContentInterface;
          if (errors_0.fields) {
            if (errors_0.fields.recipientAddress) {
              setErrors(prev => ({
                ...prev,
                recipient: errors_0.fields.recipientAddress?.join('')
              }));
            }
            const currentEmail = errors_0.fields.mailing?.currentEmail;
            if (currentEmail) {
              setErrors(prev_0 => ({
                ...prev_0,
                body: currentEmail.body?.join(''),
                fromEmail: currentEmail.fromEmail?.join(''),
                fromName: currentEmail.fromName?.join(''),
                subject: currentEmail.subject?.join(''),
                previewText: currentEmail.previewText?.join('')
              }));
            }
          }
        } else {
          const errors_1 = e.errors as EmailErrorResponseContentInterface;
          if (errors_1.fields) {
            const fields = errors_1.fields as unknown as Record<keyof EmailBaseInterface, string[]>;
            setErrors(prev_1 => ({
              ...prev_1,
              body: fields.body?.join(''),
              fromEmail: fields.fromEmail?.join(''),
              fromName: fields.fromName?.join(''),
              subject: fields.subject?.join(''),
              previewText: fields.previewText?.join('')
            }));
          }
        }
        if (e.errors.common) {
          const commonErrors = e.errors.common;
          setErrors(prev_2 => {
            return {
              ...prev_2,
              common: commonErrors
            };
          });
        }
      } else {
        toast.error(t('global.error'));
      }
    }
  };
  const {
    UnsavedEmailConfirmationModal,
    removeDbRecord,
    setNeedToShowModal
  } = useUnsavedEmail({
    type: withRecipient ? UnsavedEmailsEnum.create_rule_email_with_recipient : UnsavedEmailsEnum.create_rule_email,
    currentBody: tempState?.body,
    initialBody: '',
    onPaste: (data_1: string) => {
      setTempState(prev_5 => ({
        ...prev_5,
        body: data_1
      }));
    }
  });
  const onChangeEmail = (value: EmailRecipientInterface) => setTempState(value);
  return <>
      <Tooltip mode={'hover'} label={t('dashboard.automation_rule.edit.create_email')}>
        <button onClick={() => setOpened(true)} className={'group outline-hidden'}>
          <AddIcon className={'fill-fill-primary main-transition-colors group-hover:fill-brand-primary group-focus-visible:fill-brand-primary'} />
        </button>
      </Tooltip>
      <Modal afterLeave={() => {
      setErrors(defaultErrors);
      setTempState(defaultValues);
      setAttachmentsTempState([]);
    }} afterEnter={() => setNeedToShowModal(true)} isFetching={isFetching} opened={opened} onClose={() => setOpened(false)} title={t('dashboard.automation_rule.edit.create_email')} size={ModalSizeEnum.big}>
        <form className="flex flex-col gap-6 lg:gap-10">
          <DashboardMailEditor withRecipient={withRecipient} inModal isPreFetching={false} tempState={tempState} onChangeEmail={onChangeEmail} attachmentsTempState={attachmentsTempState} setAttachmentsTempState={setAttachmentsTempState} errors={errors} />
          <div className={'flex justify-center'}>
            <PrimaryButton width="large" type={'submit'} onClick={async (e_0: React.MouseEvent<HTMLButtonElement>) => {
            e_0.preventDefault();
            onCreate();
          }} isFetching={isFetching}>
              {t('global.save')}
            </PrimaryButton>
          </div>
        </form>
        {UnsavedEmailConfirmationModal}
      </Modal>
    </>;
};
export default RuleEmailCreateModal;